<!-- =============================================================================
     CLIENT-SIDE ENCRYPTION PASSWORD DIALOG
     Console surface, opt-in via cssClass="fn-dialog fn-encryption-dialog"
     (styling lives in src/styles/_dialog.scss — Syncfusion relocates .e-dialog
     out of this component's host, so :host-context tokens cannot reach it).

     Structure only. Every binding, form control, validator, template ref and
     click handler is unchanged; the stock Material radios / outlined fields /
     grey slab buttons are replaced by the Console option-card + boxed-field +
     action-row recipe used by the bookmark dialog and the Client-Side Caching
     page this dialog is launched from.
     ========================================================================= -->
<ejs-dialog #ejDialogSettings [showCloseIcon]="false" cssClass="fn-dialog fn-encryption-dialog"
  [header]="state === 'FillPassword' ? 'Client-Side Encryption Password': 'Password is not Correct'"
  [visible]="isVisible" [animationSettings]="animationSettings" [enableResize]='false' [isModal]="true"
  (beforeClose)="validation($event)">
  <ng-template #content>
    <div class="app-encryption-password encDlg">
      <ng-container *ngIf="!isPasswordSetup;else AlreadySetupPasswordTemplate">

        <p class="encLead">
          Choose how you will unlock the encrypted copy of your notebooks that is kept on this device.
        </p>

        <!-- The choice, as two option cards. The radio inputs and the
             (change)="onTypeChange($event)" binding are untouched — only the
             projected label content and the styling changed.

             A complex password is the recommendation: it is the difference
             between a cache an attacker can brute-force in minutes and one
             they cannot. The card says so twice — a badge you cannot miss and
             the copy underneath — and the PIN's trade-off is stated plainly
             rather than softened to "moderately secure". -->
        <mat-radio-group class="encChoices" [value]="type" color="primary" (change)="onTypeChange($event)">
          <mat-radio-button class="radio-spacing encChoice" [value]="'PIN'">
            <span class="encChoiceIco"><mat-icon>dialpad</mat-icon></span>
            <span class="encChoiceCopy">
              <b>PIN</b>
              <span>8 digits. Quick to enter, but far easier to guess than a password.</span>
            </span>
          </mat-radio-button>
          <mat-radio-button class="radio-spacing encChoice encChoice--best" [value]="'Password'">
            <span class="encChoiceIco"><mat-icon>password</mat-icon></span>
            <span class="encChoiceCopy">
              <b>Password <span class="encChoiceBadge">Recommended</span></b>
              <span>
                A <b>complex password</b> &mdash; 8+ characters mixing upper and lower case, numbers and
                symbols &mdash; gives the strongest protection for the notes cached on this device.
              </span>
            </span>
          </mat-radio-button>
        </mat-radio-group>

        <!--
          What this secret does, stated once. Reassuring on purpose: forgetting
          it costs a reset, not your notes — everything timestamped is already
          on the server. The only real exposure is a note that never synced,
          which is the rare case, so it is a footnote and not the headline.
        -->
        <div class="encNote">
          <mat-icon>lock</mat-icon>
          <div>
            This PIN or password is never sent to us and is not stored anywhere &mdash; it is what unlocks the
            encrypted cache on <b>this device</b>. Forget it and you can simply reset it: your notes live on the
            server, so nothing is lost&nbsp;&mdash; unless a note had not synced yet.
          </div>
        </div>

        <ng-container *ngIf="type === 'Password'">
          <ng-container [formGroup]="setupPassword">

            <div class="encField">
              <label class="encLabel" for="encSetupPassword">Password</label>
              <div class="encInputWrap">
                <input id="encSetupPassword" class="encInput" [type]="hidePassword ? 'password' : 'text'"
                  formControlName="password" placeholder="Enter your password"
                  (input)="onPasswordInput($any($event.target).value)">
                <button type="button" class="encEye" tabindex="-1" (click)="hidePassword = !hidePassword"
                  [attr.aria-label]="'Hide password'" [attr.aria-pressed]="hidePassword">
                  <mat-icon>{{hidePassword ? 'visibility_off' : 'visibility'}}</mat-icon>
                </button>
              </div>
              <!-- mat-error only rendered once the field had been touched (Material's
                   default ErrorStateMatcher); the touched/dirty guard reproduces that,
                   otherwise "Field is required" would greet the user on open. -->
              <div class="encError"
                *ngIf="setupPassword.get('password')?.errors && (setupPassword.get('password')?.touched || setupPassword.get('password')?.dirty)">
                {{setupPassword.controls.password.errors | pipeFunction: decorateMessageError}}</div>

              <!--
                Strength readout. Advisory only - Save is still gated by the
                minLength(8) validator, not by this. See
                shared/Utility/passwordStrength.ts for how it is scored and why
                length outweighs symbol soup.
              -->
              <div class="encStrength" *ngIf="strengthLabel" [attr.data-level]="strengthLevel">
                <div class="encStrengthBar" role="progressbar" [attr.aria-valuenow]="strengthPercent"
                  aria-valuemin="0" aria-valuemax="100" [attr.aria-label]="'Password strength: ' + strengthLabel">
                  <span [style.width.%]="strengthPercent"></span>
                </div>
                <!-- Verdict and hint are two separate statements and must never
                     read as one word ("WeakLength helps most:"). Angular drops
                     the whitespace between sibling elements, so the hint is a
                     <div> — block-level even with no CSS at all — and it is
                     omitted entirely when there is nothing to advise. -->
                <div class="encStrengthRow">
                  <span class="encStrengthLabel">{{strengthLabel}}</span>
                  <div class="encStrengthAdvice" *ngIf="strengthAdvice">{{strengthAdvice}}</div>
                </div>
              </div>
            </div>

            <div class="encField">
              <label class="encLabel" for="encConfirmPassword">Confirm password</label>
              <div class="encInputWrap">
                <input id="encConfirmPassword" class="encInput" [type]="hidePassword ? 'password' : 'text'"
                  formControlName="confirmPassword" placeholder="Confirm your password">
                <button type="button" class="encEye" tabindex="-1" (click)="hidePassword = !hidePassword"
                  [attr.aria-label]="'Hide password'" [attr.aria-pressed]="hidePassword">
                  <mat-icon>{{hidePassword ? 'visibility_off' : 'visibility'}}</mat-icon>
                </button>
              </div>
              <div class="encError"
                *ngIf="setupPassword.get('confirmPassword')?.errors && (setupPassword.get('confirmPassword')?.touched || setupPassword.get('confirmPassword')?.dirty)">
                {{setupPassword.controls.confirmPassword.errors | pipeFunction: decorateMessageError}}</div>
            </div>

          </ng-container>

          <!-- No Cancel here: the dialog is deliberately undismissable in this
               state (showCloseIcon=false + validation() cancels beforeClose). -->
          <div class="encActions">
            <span class="encActionsSpacer"></span>
            <button type="button" [disabled]="setupPassword.invalid" class="encBtn encBtnPrimary" id="btnSubmit"
              #btnSubmit (click)="onSetupPassword()">Save</button>
          </div>
        </ng-container>

        <ng-container *ngIf="type === 'PIN'">
          <mat-tab-group #pinTabs [selectedIndex]="selectedIndex" (selectedTabChange)="keepPinScroll()"
            (animationDone)="revealPinStep()">
            <mat-tab>
              <div class="encPinStep" #pinStep *ngIf="selectedIndex === 0">
                <div class="encLabel encLabel--block encLabel--center">Choose your 8-digit PIN</div>
                <app-pin-numpad [length]="8" (onChange)="onChangeNumpadSetupPassword($event, 'password')"></app-pin-numpad>
                <div class="encActions">
                  <span class="encActionsSpacer"></span>
                  <button type="button" [disabled]="setupPassword.get('password').invalid"
                    class="encBtn encBtnPrimary" (click)="selectedIndex = 1">Confirm</button>
                </div>
              </div>
            </mat-tab>
            <mat-tab>
              <div class="encPinStep" #pinStep *ngIf="selectedIndex === 1">
                <ng-container *ngIf="!showError">
                  <div class="encLabel encLabel--block encLabel--center">Please confirm your PIN</div>
                </ng-container>
                <div *ngIf="showError" class="encError encError--block">PIN is not match</div>
                <app-pin-numpad [length]="8" (onChange)="onChangeNumpadSetupPassword($event, 'confirmPassword')"></app-pin-numpad>
                <div class="encActions">
                  <button type="button" class="encBtn encBtnGhost" (click)="onBack()">Back</button>
                  <span class="encActionsSpacer"></span>
                  <button type="button" [disabled]="setupPassword.get('confirmPassword').value?.length < 8"
                    class="encBtn encBtnPrimary" id="btnSubmit" #btnSubmit (click)="onSetupPIN()">Save</button>
                </div>
              </div>
            </mat-tab>
          </mat-tab-group>
        </ng-container>
      </ng-container>
    </div>

  </ng-template>
</ejs-dialog>

<ng-template #DeletePasswordConfirmationTemplate>
  <div class="encNote encNote--danger">
    <mat-icon>error_outline</mat-icon>
    <div>
      You have entered an incorrect {{type}}. This {{type}} is used to encrypt and decrypt notes on your device.
    </div>
  </div>

  <div class="encLabel encLabel--block">Options</div>
  <ul class="encOptions">
    <li><b>Re-enter {{type}}:</b> Try entering your {{type}} again.</li>
    <li><b>Delete Local Cache:</b> Remove all unsynced notes stored on this device. This action cannot be undone.
      ({{numberOffline.notes + numberOffline.notebooks}} Notes/ Notebooks will be lost).</li>
  </ul>

  <div class="encActions">
    <button type="button" class="encBtn encBtnGhost" (click)="onDeleteCancel()">Re-Enter {{type}}</button>
    <span class="encActionsSpacer"></span>
    <button type="button" class="encBtn encBtnDelete" id="btnSubmit" #btnSubmit
      (click)="onDeleteOldEncriptionPassword()">Delete Local Cache</button>
  </div>
</ng-template>

<ng-template #AlreadySetupPasswordTemplate>
  <ng-container *ngIf="state === 'FillPassword'; else DeletePasswordConfirmationTemplate">
    <ng-container *ngIf="type === 'Password'">
      <ng-container *ngTemplateOutlet="VerifyPassword"></ng-container>
    </ng-container>
    <ng-container *ngIf="type === 'PIN'">
      <ng-container *ngTemplateOutlet="VerifyPIN"></ng-container>
    </ng-container>
  </ng-container>
</ng-template>

<ng-template #VerifyPassword>
  <p class="encLead">
    Enter the password that unlocks the encrypted cache on this device.
  </p>
  <div class="encField">
    <label class="encLabel" for="encVerifyPassword">Password</label>
    <div class="encInputWrap">
      <input id="encVerifyPassword" class="encInput" [type]="hidePassword ? 'password' : 'text'"
        [formControl]="passwordControl" placeholder="Enter your password">
      <button type="button" class="encEye" (click)="hidePassword = !hidePassword" [attr.aria-label]="'Hide password'"
        [attr.aria-pressed]="hidePassword">
        <mat-icon>{{hidePassword ? 'visibility_off' : 'visibility'}}</mat-icon>
      </button>
    </div>
    <div class="encError" *ngIf="passwordControl.errors && passwordControl.errors.minlength">Minimum
      {{passwordControl.errors.minlength.requiredLength}} Characters</div>
  </div>
  <div class="encActions">
    <span class="encActionsSpacer"></span>
    <button type="button" [disabled]="passwordControl.invalid" class="encBtn encBtnPrimary" id="btnSubmit" #btnSubmit
      (click)="onSavePassword()">Submit</button>
  </div>
</ng-template>
<ng-template #VerifyPIN>
  <p class="encLead">
    Enter the PIN that unlocks the encrypted cache on this device.
  </p>
  <app-pin-numpad [length]="8" (onChange)="onChangeNumpad($event)"></app-pin-numpad>
  <div class="encActions">
    <span class="encActionsSpacer"></span>
    <button type="button" [disabled]="passwordControl.invalid" class="encBtn encBtnPrimary" id="btnSubmit" #btnSubmit
      (click)="onSavePassword()">Submit</button>
  </div>
</ng-template>
